import { useMemo } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { isAdministrativeStaffRole, isStaffRole, isTechnicalStaffRole } from '@/types';
import type { Ruolo } from '@/types';

/** Named capabilities, so components ask "can I?" instead of "which role am I?". */
export interface Permissions {
  role: Ruolo | null;
  /** Internal staff: sees every tenant. */
  isStaff: boolean;
  isAdmin: boolean;
  isOperator: boolean;
  isAdministrativeOperator: boolean;
  /** Tenant administrator: manages users inside their own tenant. */
  isTenantAdmin: boolean;
  isTenantUser: boolean;
  /** Session tenant is a billed partner (not a cliente finale). */
  isPartnerAdmin: boolean;
  /** Session tenant is a cliente finale under a partner. */
  isClienteFinaleAdmin: boolean;

  canManageClienti: boolean;
  /** Framework matrix toggle (admin + operatore_amministrativo). */
  canManageFrameworkMatrix: boolean;
  /** Anagrafica: admin and billing operator. Technical operators cannot. */
  canCreateClienti: boolean;
  canUpdateClienti: boolean;
  /** Create `tipo=partner` tenants. Staff only. */
  canCreatePartner: boolean;
  /** Create clienti finali under a partner. Staff or partner admin. */
  canCreateClienteFinale: boolean;
  canManageContratti: boolean;
  canManageMacchine: boolean;
  canManageProdotti: boolean;
  canViewTicketQueue: boolean;
  canViewTasks: boolean;
  canAssignTickets: boolean;
  canChangeTicketCategory: boolean;
  canManageTicketCategories: boolean;
  canManageOperatorCategories: boolean;
  /** Only staff may move a ticket to `risolto`/`chiuso`. */
  canResolveTickets: boolean;
  /** Super admin (`admin`) may reopen a closed ticket. */
  canReopenClosedTickets: boolean;
  canCreateTickets: boolean;
  /**
   * Open a ticket against a cliente finale (not the partner itself).
   * Staff, partner admin, and cliente finale admin. Partner `cliente_user` cannot.
   */
  canOpenTicketOnClienteFinale: boolean;
  /** Full ticket inbox with filters. Hidden from `cliente_user`. */
  canBrowseTickets: boolean;
  canCommentTickets: boolean;
  canManageUsers: boolean;
  /** Invite / create accounts. Billing staff cannot. */
  canCreateUsers: boolean;
  /** Invite on a partner tenant. Staff or partner admin. */
  canCreateUserOnPartner: boolean;
  /** Invite on a linked cliente finale. Staff or partner admin. */
  canCreateUserOnClienteFinale: boolean;
  /** Activate / deactivate users. Billing staff cannot. */
  canChangeUtenteStato: boolean;
  /** Change an existing user's role. Billing staff cannot. */
  canChangeUtenteRuolo: boolean;
  /** Hard-delete accounts: platform admin (any except self) or tenant admin (own company, except self). */
  canDeleteUsers: boolean;
  canManageApiKeys: boolean;
  /** Register and manage Vaultwarden instances / memberships (platform admin only). */
  canManageVaultwarden: boolean;
  canViewAggregatedReports: boolean;
  canViewCedolini: boolean;
  /** Importi, listini e tariffe. Nascosti al cliente finale. */
  canViewCedolinoBilling: boolean;
  /** Prezzi e importi. Partner e staff sì, cliente finale no. */
  canViewPrices: boolean;
  /** Scadenze e badge SLA. Solo staff Wenkey. */
  canViewSla: boolean;
  /** Priorità, fonte e categoria in creazione/modifica ticket: solo staff. */
  canClassifyTickets: boolean;
  canManageCedolini: boolean;
  /** Quote from aggregated cedolini. Every internal role. */
  canManagePreventivi: boolean;
  /** Upload shared tenant documents. */
  canUploadDocumenti: boolean;
  /** Toggle Wenkey-side document visibility (platform admin only). */
  canToggleVisibilitaWenkey: boolean;
  /** Toggle client-side document visibility. */
  canToggleVisibilitaCliente: boolean;
  /** Assign documents to specific users. */
  canAssignDocumenti: boolean;
  /** View document access audit log. */
  canViewAccessiDocumenti: boolean;
  /** Company health: dashboard, servizi, report. Hidden from `cliente_user`. */
  canViewCompanyHealth: boolean;
  canViewAuditLog: boolean;
  /** Whether the tenant switcher should be rendered. */
  canSwitchTenant: boolean;

  hasRole: (...roles: Ruolo[]) => boolean;
}

/**
 * Derives capabilities from the JWT role.
 *
 * This is UI affordance only — the backend and RLS remain the enforcement
 * point. Hiding a button the API would reject anyway is a usability decision,
 * not a security one.
 */
export function usePermissions(): Permissions {
  const role = useAuthStore((state) => state.user?.ruolo ?? null);
  const clienteTipo = useAuthStore((state) => state.user?.cliente_tipo ?? null);

  return useMemo<Permissions>(() => {
    const isAdmin = role === 'admin';
    const isOperator = role === 'operatore';
    const isAdministrativeOperator = role === 'operatore_amministrativo';
    const isStaff = isStaffRole(role);
    const isTechnical = isTechnicalStaffRole(role);
    const isBilling = isAdministrativeStaffRole(role);
    const isTenantAdmin = role === 'cliente_admin';
    const isTenantUser = role === 'cliente_user';
    const isPartnerAdmin = isTenantAdmin && clienteTipo === 'partner';
    const isClienteFinaleAdmin = isTenantAdmin && clienteTipo === 'cliente_finale';
    const canViewPrices = isStaff || clienteTipo !== 'cliente_finale';
    const canCreateUsers = (isStaff && !isAdministrativeOperator) || isTenantAdmin;

    return {
      role,
      isStaff,
      isAdmin,
      isOperator,
      isAdministrativeOperator,
      isTenantAdmin,
      isTenantUser,
      isPartnerAdmin,
      isClienteFinaleAdmin,

      canManageClienti: isStaff,
      canManageFrameworkMatrix: isBilling,
      canCreateClienti: isBilling,
      canUpdateClienti: isBilling,
      canCreatePartner: isStaff,
      canCreateClienteFinale: isStaff || isPartnerAdmin,
      canManageContratti: isStaff,
      canManageMacchine: isTechnical,
      canManageProdotti: isAdmin,
      canViewTicketQueue: isTechnical,
      canViewTasks: isStaff,
      canAssignTickets: isTechnical,
      canChangeTicketCategory: isTechnical,
      canManageTicketCategories: isAdmin,
      canManageOperatorCategories: isAdmin,
      canResolveTickets: isTechnical,
      /** Only the platform super admin may reopen a closed ticket. */
      canReopenClosedTickets: isAdmin,
      canCreateTickets: role !== null,
      canOpenTicketOnClienteFinale: isStaff || isTenantAdmin,
      canBrowseTickets: role !== null && !isTenantUser,
      canCommentTickets: role !== null,
      canManageUsers: isStaff || isTenantAdmin,
      canCreateUsers,
      canCreateUserOnPartner: (isStaff && !isAdministrativeOperator) || isPartnerAdmin,
      canCreateUserOnClienteFinale: (isStaff && !isAdministrativeOperator) || isPartnerAdmin,
      canChangeUtenteStato: (isStaff && !isAdministrativeOperator) || isTenantAdmin,
      canChangeUtenteRuolo: (isStaff && !isAdministrativeOperator) || isTenantAdmin,
      canDeleteUsers: isAdmin || isTenantAdmin,
      canManageApiKeys: isTechnical,
      canManageVaultwarden: isAdmin,
      canViewAggregatedReports: isStaff,
      canViewCedolini: isStaff || (isTenantAdmin && canViewPrices),
      canViewCedolinoBilling: canViewPrices && (isStaff || isTenantAdmin),
      canViewPrices,
      canViewSla: isStaff,
      canClassifyTickets: isStaff,
      canManageCedolini: isBilling,
      canManagePreventivi: isStaff,
      canUploadDocumenti: isStaff || isTenantAdmin,
      canToggleVisibilitaWenkey: isAdmin,
      canToggleVisibilitaCliente: isAdmin || isTenantAdmin,
      canAssignDocumenti: isAdmin || isTenantAdmin,
      canViewAccessiDocumenti: isAdmin || isTenantAdmin || isAdministrativeOperator,
      canViewCompanyHealth: isTechnical || isTenantAdmin,
      canViewAuditLog: isStaff || isTenantAdmin,
      canSwitchTenant: isStaff,

      hasRole: (...roles: Ruolo[]) => role !== null && roles.includes(role),
    };
  }, [role, clienteTipo]);
}
